import { ACOES, RECURSOS, type Acao, type Recurso } from '@locaveri/shared'

import type { PermissaoDaApi } from './api'

/**
 * A MATRIZ DE PERMISSÕES, sem React.
 *
 * A tela desenha; as decisões moram aqui. Duas delas são as travas que a API
 * impõe com 403, e errá-las não produz tela feia — produz uma edição que o
 * gestor faz, salva, e descobre que não valeu:
 *
 *   1. NINGUÉM CONCEDE O QUE NÃO TEM. Sem isso, dá para criar um papel com
 *      `payment.export`, atribuí-lo a si mesmo e exportar — escalada de
 *      privilégio por interposta pessoa, sem tocar em nenhum `if`.
 *   2. O SUPER_ADMIN É INTOCÁVEL. É o único papel capaz de reabrir o controle
 *      de acesso; se pudesse perder permissão, daria para trancar a instalação
 *      inteira do lado de fora, sem caminho de volta por tela nenhuma.
 *
 * A terceira decisão é de leitura, e é a que mais confunde quando falta:
 * célula VAZIA e caixa DESMARCADA não são a mesma coisa. Vazia é "esta
 * combinação não existe no catálogo" — não há `dashboard.approve`. Desmarcada é
 * "existe, e este perfil não tem". Uma tela que mostrasse as duas iguais faria
 * o gestor procurar para sempre a caixa que nunca vai aparecer.
 */

/** A chave canônica do catálogo: `recurso.acao`. */
export function chaveDaPermissao(recurso: Recurso, acao: Acao): string {
  return `${recurso}.${acao}`
}

export interface LinhaDeRecurso {
  readonly recurso: Recurso
  /** `acao` → a permissão do catálogo. Ausente = combinação que não existe. */
  readonly porAcao: ReadonlyMap<Acao, PermissaoDaApi>
}

/**
 * Agrupa o catálogo por recurso.
 *
 * A ordem é a de `RECURSOS` — a declarada no domínio —, e não a alfabética nem
 * a que a API devolveu. Ordenar por nome traduzido faria a lista mudar de ordem
 * ao traduzir um rótulo; ordenar pela resposta faria a tela depender de um
 * `ORDER BY` que ninguém prometeu.
 *
 * Recurso sem nenhuma permissão no catálogo não vira linha: seria uma linha de
 * oito traços, dizendo nada.
 */
export function agruparPorRecurso(catalogo: readonly PermissaoDaApi[]): readonly LinhaDeRecurso[] {
  const indice = new Map<string, PermissaoDaApi>(catalogo.map((item) => [item.chave, item]))

  return RECURSOS.map((recurso) => {
    const porAcao = new Map<Acao, PermissaoDaApi>()

    for (const acao of ACOES) {
      const encontrada = indice.get(chaveDaPermissao(recurso, acao))
      if (encontrada !== undefined) {
        porAcao.set(acao, encontrada)
      }
    }

    return { recurso, porAcao }
  }).filter((linha) => linha.porAcao.size > 0)
}

/** O que a célula é, do ponto de vista de quem olha. */
export type EstadoDaCelula =
  /** A combinação não existe no catálogo. Desenha um traço, não uma caixa. */
  | { readonly tipo: 'inexistente' }
  /** Caixa clicável. */
  | { readonly tipo: 'editavel'; readonly marcada: boolean; readonly descricao: string }
  /** Caixa visível e travada, com o motivo — botão travado sem motivo é botão quebrado. */
  | {
      readonly tipo: 'travada'
      readonly marcada: boolean
      readonly motivo: string
      readonly descricao: string
    }

export interface ContextoDaCelula {
  /** As permissões concedidas ao papel no rascunho corrente. */
  readonly marcadas: ReadonlySet<string>
  /** As permissões efetivas de QUEM ESTÁ EDITANDO. */
  readonly minhas: ReadonlySet<string>
  /** `true` para SUPER_ADMIN, e para quem não tem `role.update`. */
  readonly somenteLeitura: boolean
  readonly motivoDaLeitura: string
}

export const MOTIVO_SUPER_ADMIN =
  'O super administrador não é editável: é o único perfil capaz de reabrir o controle de acesso.'

export const MOTIVO_SEM_ESCRITA = 'Editar perfis exige a permissão role.update.'

export const MOTIVO_ALEM_DO_ALCANCE =
  'Você não tem esta permissão, então não pode concedê-la nem retirá-la de ninguém.'

/**
 * O estado de uma célula.
 *
 * A ordem dos testes importa: "não existe" vem antes de tudo, porque uma
 * combinação inexistente não é nem concedível nem negável — ela não é nada. E a
 * trava de alcance é testada DEPOIS da de leitura para que o SUPER_ADMIN
 * explique a si mesmo em vez de dizer "você não tem esta permissão", que seria
 * verdade e ainda assim a resposta errada.
 */
export function estadoDaCelula(
  linha: LinhaDeRecurso,
  acao: Acao,
  contexto: ContextoDaCelula,
): EstadoDaCelula {
  const permissao = linha.porAcao.get(acao)

  if (permissao === undefined) {
    return { tipo: 'inexistente' }
  }

  const marcada = contexto.marcadas.has(permissao.chave)

  if (contexto.somenteLeitura) {
    return {
      tipo: 'travada',
      marcada,
      motivo: contexto.motivoDaLeitura,
      descricao: permissao.descricao,
    }
  }

  // Vale para os DOIS sentidos, e o de tirar é o menos óbvio: quem não tem a
  // permissão também não pode retirá-la de um papel que a tem, senão a edição
  // seria uma via de mão única — dá para apagar e não dá para devolver.
  if (!contexto.minhas.has(permissao.chave)) {
    return {
      tipo: 'travada',
      marcada,
      motivo: MOTIVO_ALEM_DO_ALCANCE,
      descricao: permissao.descricao,
    }
  }

  return { tipo: 'editavel', marcada, descricao: permissao.descricao }
}

/**
 * Quantas concessões o rascunho mudou.
 *
 * Diferença simétrica, e não `tamanho do rascunho - tamanho original`: marcar
 * uma e desmarcar outra é DUAS alterações, e a subtração de tamanhos diria
 * zero — deixando o botão "Salvar" apagado com duas mudanças pendentes na tela.
 */
export function contarAlteracoes(
  rascunho: ReadonlySet<string>,
  original: ReadonlySet<string>,
): number {
  let total = 0

  for (const chave of new Set([...rascunho, ...original])) {
    if (rascunho.has(chave) !== original.has(chave)) {
      total += 1
    }
  }

  return total
}
